import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useUserModel } from '@/hooks/useUserModel';
import { i18n } from '@/utils/i18n';
import { clientKeys } from '@/config/clientKeys';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@vaiu/ui/card";
import NotificationSettings from '@/components/NotificationSettings';
import NotificationEventPreferences from '@/components/NotificationEventPreferences';
import LanguageSelector from '@/components/LanguageSelector';
import BiometricSettings from '@/components/BiometricSettings';
import PhoneNumberManager from '@/components/profile/PhoneNumberManager';
import { ProfileTransferOutPayoutsCard } from '@/components/profile/ProfileTransferOutPayoutsCard';
import BrandThemeSelector from '@/components/BrandThemeSelector';
import CommunicationPreferences from '@/components/CommunicationPreferences';
import { Button } from "@vaiu/ui/button";
import { Input } from "@vaiu/ui/input";
import { Label } from "@vaiu/ui/label";
import { Badge } from "@vaiu/ui/badge";
import { Separator } from "@vaiu/ui/separator";
import { Switch } from "@vaiu/ui/switch";
import { Textarea } from "@vaiu/ui/textarea";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@vaiu/ui/dialog";
import { 
  User, 
  Mail, 
  Shield, 
  Calendar, 
  ArrowLeft,
  Wallet,
  Phone,
  MapPin,
  Building,
  Settings,
  Edit,
  Save,
  X,
  Plus,
  Minus,
  Trash2,
  CheckCircle,
  AlertCircle,
  Star,
  Globe,
  Code,
  FileText,
  AlertTriangle,
  ShoppingBag,
  Store,
  PiggyBank,
  Home,
  CreditCard
} from 'lucide-react';
import { toast } from "sonner";
import { CurrencyDisplay } from '@vaiu/ui';
import LandingPreferenceSelector from '@/components/LandingPreferenceSelector';
import { platformApi } from '@/services/platformApi'
import { currencyService } from '@/services/currencyService';
import { MmMoneyAppearanceCard, type MeMoneyLocale, type MeMoneyToastFn } from '@vaiu/memoney';
import {
  extractUserMmMoneyBrandingFromProfile,
  hasMerchantProfileContextForMmMoney,
  isMmMoneyCurrency,
  mergeMmMoneyBrandingPreferUser,
  normalizeMmMoneyBrandingFromCurrencyApiPayload,
  resolveAuthUserId,
} from '@vaiu/utils/mmMoney';

import {
  mergeStripeConnectFromMerchantStatus,
  mergeStripeConnectIdIntoProfile,
} from '@/utils/profileStripeConnect';

// Google Maps integration
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

interface ProfileData {
  _id: string;
  username: string;
  emails: Array<{
    address: string;
    verified: boolean;
  }>;
  telephone: {
    number: string;
    verified: boolean;
  };
  profile: {
    firstName?: string;
    lastName?: string;
    paypalEmail?: string;
    /** false = must re-verify; undefined on legacy accounts treated as verified if paypalEmail set */
    paypalEmailVerified?: boolean;
    transferOutPreferences?: Record<string, { enabled: boolean }>;
    stripeConnectId?: string;
    /** Some API responses use snake_case only */
    stripe_connect_id?: string;
  };
  addresses: Array<{
    id: string;
    lat: number;
    lng: number;
    address: string;
    detail: string;
    isPrimary?: boolean;
  }>;
  roles: string[];
  otherProfiles: {
    merchant?: any;
  };
  balances: Record<string, number>;
  currency: string;
  hasPin: boolean;
  pinEnabled: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  isVerified: boolean;
  active: boolean;
  createdAt: string;
  lastLoginAt: string;
}

interface AddressFormData {
  lat: number;
  lng: number;
  address: string;
  detail: string;
  isPrimary: boolean;
}

const ProfilePage = () => {
  const { user: authUser, fetchProfile: refreshAuthProfile } = useAuth();
  const { user: userModel, primaryRole, permissions } = useUserModel();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Get token from localStorage where AuthContext stores it
  const token = localStorage.getItem('authToken');
  
  // State management
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [tempData, setTempData] = useState<any>({});
  const [forceUpdate, setForceUpdate] = useState(0); // For language changes

  // Form states
  const [pinData, setPinData] = useState({ oldPin: '', newPin: '', confirmPin: '' });
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState<AddressFormData>({
    lat: 0,
    lng: 0,
    address: '',
    detail: '',
    isPrimary: false
  });
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);

  // Developer toggle states
  const [showDeveloperTermsSection, setShowDeveloperTermsSection] = useState(false);
  const [pendingDeveloperToggle, setPendingDeveloperToggle] = useState(false);

  // Auto-deposit preferences state
  const [autoDepositPreferences, setAutoDepositPreferences] = useState<{
    autoDepositEnabled: boolean;
    autoDepositThreshold?: number;
  }>({
    autoDepositEnabled: false,
    autoDepositThreshold: 0
  });

  // Auto-accept preferences state for token redemption
  const [autoAcceptPreferences, setAutoAcceptPreferences] = useState<{
    autoAcceptTokens: boolean;
  }>({
    autoAcceptTokens: false
  });

  // Invoice payment preferences state
  const [invoicePaymentPreferences, setInvoicePaymentPreferences] = useState<{
    mode: 'auto-pay' | 'always-ask' | 'ask-if-above';
    threshold?: number;
  }>({
    mode: 'always-ask',
    threshold: 0
  });

  const hasMerchantRole = useMemo(
    () =>
      Boolean(
        profile &&
          Array.isArray(profile.roles) &&
          profile.roles.includes('MERCHANT')
      ),
    [profile]
  );

  // Google Maps states
  const [map, setMap] = useState<any>(null);
  const [marker, setMarker] = useState<any>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerInstanceRef = useRef<any>(null);

  const API_BASE = ''; // Use relative URLs like AuthContext
  

  // Load Google Maps API (prevent multiple loads)
  const loadGoogleMaps = useCallback(() => {
    if (window.google && window.google.maps) {
      return Promise.resolve();
    }
    
          // Check if script is already loading
      const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
      if (existingScript) {
        return new Promise<void>((resolve) => {
          const checkGoogle = () => {
            if (window.google && window.google.maps) {
              resolve();
            } else {
              setTimeout(checkGoogle, 100);
            }
          };
          checkGoogle();
        });
      }
    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${clientKeys.GOOGLE_MAPS_API_KEY}&libraries=places,marker&loading=async&callback=initMap`;
      script.async = true;
      script.defer = true;
      
      window.initMap = () => {
        resolve();
      };
      
      script.onload = () => {
        // Sometimes the callback doesn't fire, so check manually
        if (window.google && window.google.maps) {
          resolve();
        }
      };
      
      script.onerror = (error) => {
        console.error('❌ Failed to load Google Maps API:', error);
        reject(new Error('Failed to load Google Maps API - Check your internet connection and API key'));
      };
      
      document.head.appendChild(script);
    });
  }, []);

  // API helper function - matches AuthContext approach
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    const url = endpoint; // Use relative URLs, proxy handles routing
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || errorData.message || `API Error: ${response.status}`);
    }

    return response.json();
  };

  const refreshProfileAddresses = async () => {
    try {
      const userAddresses = await platformApi.user.getUserAddresses();
      setProfile((prev) => (prev ? { ...prev, addresses: userAddresses } : prev));
      return userAddresses;
    } catch (err) {
      console.warn('GET /user/addresses failed, falling back to profile:', err);
      const data = await apiCall('/api/v1/profile');
      const addresses = Array.isArray(data.addresses) ? data.addresses : [];
      setProfile((prev) => (prev ? { ...prev, addresses } : prev));
      return addresses;
    }
  };

  // Load profile data
  const loadProfile = async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      const data = await apiCall('/api/v1/profile');
      
      // Load user addresses from the API endpoint
      try {
        const userAddresses = await platformApi.user.getUserAddresses();
        // Use addresses from the dedicated endpoint
        data.addresses = userAddresses;
        console.log('✅ Loaded addresses from API:', userAddresses);
      } catch (err: any) {
        console.error('Failed to load user addresses:', err);
        // Ensure addresses is always an array even if API fails
        if (!Array.isArray(data.addresses)) {
          data.addresses = [];
        }
      }

      if (data.profile == null || typeof data.profile !== 'object') {
        data.profile = {};
      }

      // PATCH /profile stores names under profile.*; some responses also expose top-level fields
      if (data.firstName && !data.profile.firstName) {
        data.profile.firstName = data.firstName;
      }
      if (data.lastName && !data.profile.lastName) {
        data.profile.lastName = data.lastName;
      }

      mergeStripeConnectIdIntoProfile(data);
      await mergeStripeConnectFromMerchantStatus(data, () =>
        platformApi.merchant.getMerchantStripeConnectStatus()
      );

      setProfile(data);
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error(i18n.__('profile.toast.failedToLoad'));
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  const loadProfileRef = useRef(loadProfile);
  loadProfileRef.current = loadProfile;

  const meMoneyToast = useMemo<MeMoneyToastFn>(
    () => (props) => {
      const { title, description, variant } = props;
      if (variant === 'destructive') {
        toast.error(title, { description: description ?? '' });
      } else {
        toast.success(title, { description: description ?? '' });
      }
    },
    []
  );

  const mmMoneyBrandingHandlers = useMemo(
    () => ({
      load: async () => {
        const data = await platformApi.getProfile();
        let branding = extractUserMmMoneyBrandingFromProfile(data);
        try {
          const listing = await platformApi.getIssuerMeMoneyCurrency();
          const fromCurrency = normalizeMmMoneyBrandingFromCurrencyApiPayload(listing);
          branding = mergeMmMoneyBrandingPreferUser(branding, fromCurrency);
        } catch {
          /* optional — older gateways */
        }
        const hasUserArt = Boolean(branding.backgroundImageUrl || branding.currencyIconUrl);
        if (hasUserArt) return branding;
        if (hasMerchantProfileContextForMmMoney(data)) {
          try {
            const mp = await platformApi.getMerchantProfile();
            const legacy = {
              backgroundImageUrl: mp.mmMoneyBranding?.backgroundImageUrl,
              currencyIconUrl: mp.mmMoneyBranding?.currencyIconUrl,
            };
            return mergeMmMoneyBrandingPreferUser(branding, legacy);
          } catch {
            return branding;
          }
        }
        return branding;
      },
      save: async (next: { backgroundImageUrl?: string; currencyIconUrl?: string }) => {
        await platformApi.patchProfile({
          profile: {
            memoney: {
              ...(next.currencyIconUrl !== undefined
                ? { iconUrl: next.currencyIconUrl || '' }
                : {}),
              ...(next.backgroundImageUrl !== undefined
                ? { backgroundUrl: next.backgroundImageUrl || '' }
                : {}),
            },
          },
          settings: {
            mmMoney: {
              backgroundImageUrl: next.backgroundImageUrl,
              currencyIconUrl: next.currencyIconUrl,
            },
          },
        });
        await loadProfileRef.current?.();
        if (token && refreshAuthProfile) {
          await refreshAuthProfile(token);
        }
      },
    }),
    [token, refreshAuthProfile]
  );

  const mmMoneyUploadImage = useCallback(
    (file: File, kind: 'icon' | 'background') => platformApi.uploadMmMoneyImageFile(file, kind),
    []
  );

  const showMeMoneyBetaBalancesNotice = useMemo(() => {
    if (!profile?.balances) return false;
    return Object.keys(profile.balances).some((code) => isMmMoneyCurrency(code));
  }, [profile?.balances]);

  // Email verification using backend API
  const verifyEmailWithToken = async (verificationToken: string) => {
    try {
      // Call the backend verification method
      const response = await apiCall('/api/v1/profile/verify-email-token', {
        method: 'POST',
        body: JSON.stringify({ token: verificationToken }),
      });
      
      toast.success(i18n.__('profile.toast.emailVerified'));
      await loadProfile(); // Refresh profile to show verified status
      
      // Remove token from URL to prevent re-verification
      searchParams.delete('token');
      navigate({ search: searchParams.toString() }, { replace: true });
      
    } catch (error: any) {
      console.error('Email verification failed:', error);
      toast.error(error.message || i18n.__('profile.toast.emailVerificationFailed'));
    }
  };

  // Check for email verification token on page load
  useEffect(() => {
    const verificationToken = searchParams.get('token');
    if (verificationToken) {
      console.log('📧 Email verification token found:', verificationToken);
      verifyEmailWithToken(verificationToken);
    }
  }, [searchParams]);

  // Load profile data on component mount
  // ProtectedRoute ensures authUser exists, so we can load immediately
  useEffect(() => {
    loadProfile();
    loadAutoDepositPreferences();
    loadAutoAcceptPreferences();
    loadInvoicePaymentPreferences();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.hash !== '#memoney') return;
    if (loading) return;
    const t = window.setTimeout(() => {
      document.getElementById('memoney')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(t);
  }, [location.hash, loading]);

  useEffect(() => {
    if (location.hash !== '#transfer-out') return;
    if (loading || !profile) return;

    setEditingSection('transfer-out');

    // Profile children (e.g. Communication preferences) load async and change height after first paint.
    // A single early scrollIntoView lands in the wrong viewport; retry after layout settles.
    const scrollTransferOut = () => {
      document.getElementById('transfer-out')?.scrollIntoView({ behavior: 'auto', block: 'start' });
    };
    const timeoutIds = [
      window.setTimeout(scrollTransferOut, 0),
      window.setTimeout(scrollTransferOut, 400),
      window.setTimeout(scrollTransferOut, 900),
    ];
    return () => timeoutIds.forEach((id) => window.clearTimeout(id));
  }, [location.hash, loading, profile]);

  // Reset map when address form closes so reopening re-initializes cleanly
  useEffect(() => {
    if (!showAddressForm) {
      setMap(null);
      setMarker(null);
      mapInstanceRef.current = null;
      markerInstanceRef.current = null;
    }
  }, [showAddressForm]);

  // Load Google Maps when address form is shown
  useEffect(() => {
    if (showAddressForm) {
      console.log('🗺️ Address form shown, loading Google Maps...');
      loadGoogleMaps().then(() => {
        console.log('✅ Google Maps loaded successfully');
        const mapElement = document.getElementById('map');
        if (mapElement && !mapInstanceRef.current) {
          initializeMap(mapElement);
        }
      }).catch((error) => {
        console.error('❌ Failed to load Google Maps:', error);
        toast.error(i18n.__('profile.toast.googleMapsFailed'));
      });
    }
  }, [showAddressForm, loadGoogleMaps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update map and marker when editing an address
  useEffect(() => {
    if (map && marker && editingAddressId && addressForm.lat !== 0 && addressForm.lng !== 0) {
      console.log('📍 Updating map for address edit:', addressForm.address);
      
      // Center map on the address coordinates
      const position = { lat: addressForm.lat, lng: addressForm.lng };
      map.setCenter(position);
      map.setZoom(15);
      
      // Update marker position
      if (marker.position !== undefined) {
        // AdvancedMarkerElement
        marker.position = position;
      } else {
        // Legacy Marker
        marker.setPosition(position);
      }
    }
  }, [map, marker, editingAddressId, addressForm.lat, addressForm.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load auto-deposit preferences
  const loadAutoDepositPreferences = async () => {
    try {
      const preferences = (await platformApi.getPreferences('auto-deposit')) as {
        autoDepositEnabled?: boolean;
        autoDepositThreshold?: number;
      };
      setAutoDepositPreferences({
        autoDepositEnabled: Boolean(preferences?.autoDepositEnabled),
        autoDepositThreshold: preferences?.autoDepositThreshold ?? 0,
      });
    } catch (error) {
      console.error('Failed to load auto-deposit preferences:', error);
      // Set default values if loading fails
      setAutoDepositPreferences({
        autoDepositEnabled: false,
        autoDepositThreshold: 0
      });
    }
  };

  // Update auto-deposit preferences
  const updateAutoDepositPreferences = async () => {
    try {
      await platformApi.updatePreferences('auto-deposit', tempData);
      
      await loadAutoDepositPreferences();
      setEditingSection(null);
      setTempData({});
      toast.success(i18n.__('profile.toast.autoDepositUpdated'));
    } catch (error) {
      console.error('Failed to update auto-deposit preferences:', error);
      toast.error(i18n.__('profile.toast.autoDepositFailed'));
    }
  };

  // Load auto-accept preferences
  const loadAutoAcceptPreferences = async () => {
    try {
      const preferences = await platformApi.getPreferences('autoAccept') as { autoAcceptTokens: boolean };
      setAutoAcceptPreferences(preferences);
    } catch (error) {
      console.error('Failed to load auto-accept preferences:', error);
      // Set default values if loading fails
      setAutoAcceptPreferences({
        autoAcceptTokens: false
      });
    }
  };

  // Update auto-accept preferences
  const updateAutoAcceptPreferences = async () => {
    try {
      await platformApi.updatePreferences('autoAccept', tempData);
      
      await loadAutoAcceptPreferences();
      setEditingSection(null);
      setTempData({});
      toast.success(i18n.__('profile.toast.autoAcceptUpdated'));
    } catch (error) {
      console.error('Failed to update auto-accept preferences:', error);
      toast.error(i18n.__('profile.toast.autoAcceptFailed'));
    }
  };

  // Load invoice payment preferences
  const loadInvoicePaymentPreferences = async () => {
    try {
      const preferences = await platformApi.getPreferences('invoicePayment') as { mode: 'auto-pay' | 'always-ask' | 'ask-if-above'; threshold?: number };
      setInvoicePaymentPreferences(preferences);
    } catch (error) {
      console.error('Failed to load invoice payment preferences:', error);
      // Set default values if loading fails
      setInvoicePaymentPreferences({
        mode: 'always-ask',
        threshold: 0
      });
    }
  };

  // Update invoice payment preferences
  const updateInvoicePaymentPreferences = async () => {
    try {
      console.log('Updating invoice payment preferences with data:', tempData);
      await platformApi.updatePreferences('invoicePayment', tempData);
      
      await loadInvoicePaymentPreferences();
      setEditingSection(null);
      setTempData({});
      toast.success(i18n.__('profile.toast.invoicePaymentUpdated'));
    } catch (error: any) {
      console.error('Failed to update invoice payment preferences:', error);
      console.error('Error message:', error?.message);
      console.error('TempData being sent:', tempData);
      
      // Show the error message from the backend
      const errorMessage = error?.message || 'Failed to update invoice payment preferences';
      toast.error(errorMessage);
    }
  };

  // Basic profile update
  const updateBasicProfile = async () => {
    try {
      await apiCall('/api/v1/profile', {
        method: 'PATCH',
        body: JSON.stringify(tempData),
      });
      
      await loadProfile();
      if (token && refreshAuthProfile) {
        await refreshAuthProfile(token);
      }
      setEditingSection(null);
      setTempData({});
      toast.success(i18n.__('profile.toast.profileUpdated'));
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(i18n.__('profile.toast.profileUpdateFailed'));
    }
  };


  // Email verification
  const sendEmailVerification = async () => {
    try {
      await apiCall('/api/v1/profile/verify-email', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      
      toast.success(i18n.__('profile.toast.verificationEmailSent'));
    } catch (error) {
      console.error('Failed to send verification email:', error);
      toast.error(i18n.__('profile.toast.verificationEmailFailed'));
    }
  };

  // PIN management
  const updatePin = async () => {
    if (pinData.newPin !== pinData.confirmPin) {
      toast.error(i18n.__('profile.toast.pinsDoNotMatch'));
      return;
    }

    try {
      await apiCall('/api/v1/profile/pin', {
        method: 'POST',
        body: JSON.stringify({
          oldPin: pinData.oldPin || undefined,
          newPin: pinData.newPin,
        }),
      });
      
      await loadProfile();
      setShowPinDialog(false);
      setPinData({ oldPin: '', newPin: '', confirmPin: '' });
      toast.success(i18n.__('profile.toast.pinUpdated'));
    } catch (error) {
      console.error('Failed to update PIN:', error);
      toast.error(i18n.__('profile.toast.pinUpdateFailed'));
    }
  };

  const togglePinEnabled = async () => {
    try {
      await apiCall('/api/v1/profile/pin/toggle', {
        method: 'POST',
      });
      
      await loadProfile();
      toast.success(i18n.__('profile.toast.pinSettingUpdated'));
    } catch (error) {
      console.error('Failed to toggle PIN:', error);
      toast.error(i18n.__('profile.toast.pinSettingFailed'));
    }
  };

  // Address management with Google Maps (Modern APIs with fallback)
  const applyAddressLocation = (lat: number, lng: number, address: string) => {
    setAddressForm((prev) => ({ ...prev, lat, lng, address }));
    const mapInstance = mapInstanceRef.current;
    const markerInstance = markerInstanceRef.current;
    if (mapInstance) {
      mapInstance.setCenter({ lat, lng });
      mapInstance.setZoom(15);
    }
    if (markerInstance) {
      if (markerInstance.position !== undefined) {
        markerInstance.position = { lat, lng };
      } else {
        markerInstance.setPosition({ lat, lng });
      }
    }
  };

  const geocodeAddressText = (addressText: string) => {
    if (!window.google?.maps?.Geocoder || !addressText.trim()) {
      toast.error('Enter an address to locate on the map');
      return;
    }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: addressText }, (results: any, status: any) => {
      if (status !== 'OK' || !results?.[0]?.geometry?.location) {
        toast.error('Could not find that address on the map');
        return;
      }
      const lat = results[0].geometry.location.lat();
      const lng = results[0].geometry.location.lng();
      const formatted = results[0].formatted_address || addressText;
      applyAddressLocation(lat, lng, formatted);
    });
  };

  const initializeMap = (mapElement: HTMLElement) => {
    if (!window.google || !mapElement) return;

    try {
      const defaultPosition = { lat: 34.052235, lng: -118.243683 }; // Default to LA
      
      const mapInstance = new window.google.maps.Map(mapElement, {
        center: defaultPosition,
        zoom: 13,
        mapId: 'DEMO_MAP_ID', // Required for AdvancedMarkerElement
        // Note: styles cannot be set when mapId is present - controlled via Cloud Console
        disableDefaultUI: false, // Keep UI controls for easier testing
      });
      
      console.log('🗺️ Map initialized successfully');

      let markerInstance: any;

      // Try to use modern AdvancedMarkerElement, fallback to legacy Marker
      if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
        // Use modern AdvancedMarkerElement
        markerInstance = new window.google.maps.marker.AdvancedMarkerElement({
          map: mapInstance,
          position: defaultPosition,
          gmpDraggable: true,
          title: "Drag me or click on the map!",
        });
        
        console.log('📍 Modern marker created successfully');

        markerInstance.addListener('dragend', (event: any) => {
          const position = markerInstance.position as any;
          const lat = position.lat;
          const lng = position.lng;
          
          setAddressForm(prev => ({ ...prev, lat, lng }));
          
          // Reverse geocoding
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              setAddressForm(prev => ({ 
                ...prev, 
                address: results[0].formatted_address 
              }));
            }
          });
        });
      } else {
        // Fallback to legacy Marker (deprecated but still works)
        console.warn('AdvancedMarkerElement not available, falling back to legacy Marker');
        markerInstance = new window.google.maps.Marker({
          map: mapInstance,
          position: defaultPosition,
          draggable: true,
          title: "Drag me or click on the map!",
        });
        
        console.log('📍 Legacy marker created successfully');

        markerInstance.addListener('dragend', (event: any) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          
          setAddressForm(prev => ({ ...prev, lat, lng }));
          
          // Reverse geocoding
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
            if (status === 'OK' && results[0]) {
              setAddressForm(prev => ({ 
                ...prev, 
                address: results[0].formatted_address 
              }));
            }
          });
        });
      }

      setMap(mapInstance);
      setMarker(markerInstance);
      mapInstanceRef.current = mapInstance;
      markerInstanceRef.current = markerInstance;
      
      // Ensure form coordinates are set to default position on map load
      setAddressForm(prev => ({
        ...prev,
        lat: prev.lat !== 0 || prev.lng !== 0 ? prev.lat : defaultPosition.lat,
        lng: prev.lat !== 0 || prev.lng !== 0 ? prev.lng : defaultPosition.lng,
      }));

      // Add click listener to map
      mapInstance.addListener('click', (event: any) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        console.log('📍 Map clicked - setting location:', { lat, lng });

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
          if (status === 'OK' && results[0]) {
            applyAddressLocation(lat, lng, results[0].formatted_address);
          } else {
            applyAddressLocation(lat, lng, `${lat.toFixed(6)}, ${lng.toFixed(6)}`);
          }
        });
      });

    } catch (error) {
      console.error('❌ Failed to initialize Google Maps:', error);
      // Show user-friendly error
      if (mapElement) {
        mapElement.innerHTML = `
          <div style="
            display: flex; 
            align-items: center; 
            justify-content: center; 
            height: 256px; 
            background: #fef2f2;
            border: 2px dashed #fca5a5;
            border-radius: 8px;
            flex-direction: column;
            color: #dc2626;
            font-size: 14px;
            text-align: center;
            padding: 20px;
          ">
            <div style="margin-bottom: 8px;">❌</div>
            <div style="font-weight: 500;">Failed to load Google Maps</div>
            <div style="font-size: 12px; margin-top: 4px;">Error: ${error.message}</div>
            <div style="font-size: 12px; margin-top: 8px;">
              <button onclick="window.location.reload()" style="
                background: #dc2626; 
                color: white; 
                padding: 4px 8px; 
                border-radius: 4px; 
                border: none; 
                cursor: pointer;
                font-size: 12px;
              ">
                Refresh Page
              </button>
            </div>
          </div>
        `;
      }
    }
  };

  const saveAddress = async () => {
    try {
      if (!addressForm.address?.trim()) {
        toast.error('Enter an address');
        return;
      }

      console.log('💾 Saving address:', {
        editingAddressId,
        addressForm,
        hasCoordinates: addressForm.lat !== 0 || addressForm.lng !== 0,
      });

      let savedAddress: { id?: string } | null = null;
      if (editingAddressId) {
        await platformApi.user.updateUserAddress(editingAddressId, {
          address: addressForm.address,
          detail: addressForm.detail,
          lat: addressForm.lat,
          lng: addressForm.lng,
          isPrimary: addressForm.isPrimary,
        });
      } else {
        savedAddress = await platformApi.user.addUserAddress({
          address: addressForm.address,
          detail: addressForm.detail || '',
          lat: addressForm.lat,
          lng: addressForm.lng,
          isPrimary: addressForm.isPrimary || false,
        });
      }

      const addresses = await refreshProfileAddresses();
      if (addresses.length === 0 && !savedAddress?.id) {
        throw new Error('Address was not saved. Please try again.');
      }

      toast.success(
        editingAddressId ? i18n.__('profile.toast.addressUpdated') : i18n.__('profile.toast.addressAdded')
      );

      setShowAddressForm(false);
      setAddressForm({ lat: 0, lng: 0, address: '', detail: '', isPrimary: false });
      setEditingAddressId(null);
    } catch (error: any) {
      console.error('❌ Failed to save address:', error);
      const message = error?.message || '';
      if (message.includes('405')) {
        toast.error('Saving addresses is not available on this server yet. Please try again after the backend is updated.');
      } else {
        toast.error(message || 'Failed to save address');
      }
    }
  };

  const deleteAddress = async (addressId: string) => {
    try {
      await platformApi.user.deleteUserAddress(addressId);
      await refreshProfileAddresses();
      toast.success(i18n.__('profile.toast.addressDeleted'));
    } catch (error) {
      console.error('Failed to delete address:', error);
      toast.error(i18n.__('profile.toast.addressDeleteError'));
    }
  };

  const setPrimaryAddress = async (addressId: string) => {
    try {
      await platformApi.user.setPrimaryUserAddress(addressId);
      await refreshProfileAddresses();
      toast.success(i18n.__('profile.toast.primaryAddressUpdated'));
    } catch (error) {
      console.error('Failed to set primary address:', error);
      toast.error(i18n.__('profile.toast.primaryAddressFailed'));
    }
  };

  // Merchant management
  const toggleMerchantStatus = async (nextChecked: boolean) => {
    if (nextChecked === hasMerchantRole) {
      return;
    }
    try {
      const action = nextChecked ? 'add' : 'remove';
      await apiCall(`/api/v1/profile/roles/${action}`, {
        method: 'POST',
        body: JSON.stringify({ role: 'MERCHANT' }),
      });
      
      await loadProfile();
      toast.success(action === 'add' ? i18n.__('profile.toast.merchantStatusEnabled') : i18n.__('profile.toast.merchantStatusDisabled'));
    } catch (error) {
      console.error('Failed to toggle merchant status:', error);
      toast.error(i18n.__('profile.toast.merchantStatusFailed'));
    }
  };

  // Developer role management
  const acceptDeveloperTerms = async () => {
    try {
      const response = await apiCall('/api/v1/profile/roles/add', {
        method: 'POST',
        body: JSON.stringify({ role: 'DEVELOPER' }),
      });
      
      console.log('✅ Developer role added successfully:', response);
      
      // Refresh profile to get updated roles
      await loadProfile();
      
      // Also refresh AuthContext to ensure it has the latest roles
      // This is important because the auth context might cache the user object
      if (refreshAuthProfile && token) {
        try {
          await refreshAuthProfile(token);
          console.log('✅ AuthContext refreshed with updated roles');
        } catch (authError) {
          console.warn('Failed to refresh AuthContext:', authError);
          // Don't fail the whole operation if auth refresh fails
        }
      }
      
      // Note: When navigating to developer.vaiu.io, the developer app will
      // automatically refresh the profile when checking roles (via ProtectedRoute)
      
      setShowDeveloperTermsSection(false);
      setPendingDeveloperToggle(false);
      toast.success(i18n.__('profile.toast.developerAccessEnabled') || 'Developer access enabled');
      
      // Note: User may need to refresh the page or log out/in on developer.vaiu.io
      // to get updated roles in the JWT token
    } catch (error: any) {
      console.error('Failed to add developer role:', error);
      const errorMessage = error?.message || i18n.__('profile.toast.developerAccessFailed') || 'Failed to enable developer access';
      toast.error(errorMessage);
      setPendingDeveloperToggle(false);
    }
  };

  const cancelDeveloperTerms = () => {
    setShowDeveloperTermsSection(false);
    setPendingDeveloperToggle(false);
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  // Format utilities
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount: number, currency: string) => {
    try {
      // Try to use the provided currency code
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(amount);
    } catch (error) {
      // If currency code is invalid (like M20), fall back to MeMoney formatting
      // Use 1 decimal place and $ symbol for custom currencies
      console.warn(`Invalid currency code "${currency}", using MeMoney formatting (1 decimal)`);
      return `$${amount.toFixed(1)}`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p>{i18n.__('vaiu.profile_page.loading') || 'Loading profile...'}</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <AlertCircle className="w-8 h-8 text-destructive mx-auto" />
          <p>{i18n.__('vaiu.profile_page.failed_to_load') || 'Failed to load profile'}</p>
          <Button onClick={loadProfile}>{i18n.__('vaiu.profile_page.retry') || 'Retry'}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center space-x-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{i18n.__('vaiu.back')}</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{i18n.__('vaiu.profile_page.title') || 'Profile Management'}</h1>
              <p className="text-sm text-muted-foreground">
                {i18n.__('vaiu.profile_page.subtitle') || ''}
              </p>
            </div>
          </div>
          
          {/* Profile status indicators */}
          <div className="flex items-center space-x-2">
            {profile.isVerified && (
              <Badge variant="default" className="flex items-center space-x-1">
                <CheckCircle className="w-3 h-3" />
                <span>Verified</span>
              </Badge>
            )}
            {hasMerchantRole && (
              <Badge variant="secondary" className="flex items-center space-x-1">
                <Building className="w-3 h-3" />
                <span>Merchant</span>
              </Badge>
            )}
          </div>
        </div>

        {/* OOP Model Information - Only shown when userModel is available */}
        {userModel && (
          <Card className="mb-6">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <CardTitle>Profile Overview</CardTitle>
              </div>
              <Badge variant="outline">{primaryRole?.displayName || 'User'}</Badge>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Display Name</p>
                  <p className="font-medium">{userModel.displayName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Full Name</p>
                  <p className="font-medium">{userModel.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Profile Completeness</p>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: `${userModel.profileCompleteness}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">{userModel.profileCompleteness}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Roles</p>
                  <div className="flex flex-wrap gap-1">
                    {userModel.roles.map(role => (
                      <Badge key={role} variant="secondary" className="text-xs">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
              {userModel.missingProfileFields.length > 0 && (
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <AlertCircle className="inline w-4 h-4 mr-1" />
                    Missing fields: {userModel.missingProfileFields.join(', ')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Personal Information */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5" />
              <CardTitle>{i18n.__('vaiu.profile_page.basic_info')}</CardTitle>
            </div>
                            <Button
                  variant="ghost"
                  size="sm"
                  data-testid="edit-basic-profile"
                  onClick={() => {
                    setEditingSection(editingSection === 'basic' ? null : 'basic');
                    setTempData({
                      username: profile.username,
                      firstName: profile.profile.firstName || '',
                      lastName: profile.profile.lastName || ''
                    });
                  }}
                >
              {editingSection === 'basic' ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {editingSection === 'basic' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="username">{i18n.__('vaiu.profile_page.username')}</Label>
                    <Input
                      id="username"
                      value={tempData.username || ''}
                      onChange={(e) => setTempData({ ...tempData, username: e.target.value })}
                      placeholder="Enter username"
                    />
                  </div>
                  <div>
                    <Label htmlFor="firstName">{i18n.__('vaiu.profile_page.first_name')}</Label>
                    <Input
                      id="firstName"
                      value={tempData.firstName || ''}
                      onChange={(e) => setTempData({ ...tempData, firstName: e.target.value })}
                      placeholder="Enter first name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">{i18n.__('vaiu.profile_page.last_name')}</Label>
                    <Input
                      id="lastName"
                      value={tempData.lastName || ''}
                      onChange={(e) => setTempData({ ...tempData, lastName: e.target.value })}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button onClick={updateBasicProfile} size="sm">
                    <Save className="w-4 h-4 mr-2" />
                    {i18n.__('common.save') || 'Save Changes'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setEditingSection(null)}
                  >
                    {i18n.__('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Username</Label>
                  <p className="text-base font-medium">{profile.username || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">First Name</Label>
                  <p className="text-base font-medium">{profile.profile.firstName || 'Not set'}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Last Name</Label>
                  <p className="text-base font-medium">{profile.profile.lastName || 'Not set'}</p>
                </div>
              </div>
            )}
            
            <Separator />
            
            <div>
              <Label className="text-sm font-medium text-muted-foreground">User ID</Label>
              <p className="text-sm font-mono text-muted-foreground">{profile._id}</p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <Mail className="w-5 h-5" />
              <CardTitle>{i18n.__('vaiu.profile_page.contact_info')}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Email Management */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Email Addresses</Label>
                {!profile.emailVerified && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={sendEmailVerification}
                  >
                    Send Verification
                  </Button>
                )}
              </div>
              
              {profile.emails.map((email, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="text-base">{email.address}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={email.verified ? "default" : "secondary"}>
                      {email.verified ? (
                        <><CheckCircle className="w-3 h-3 mr-1" />Verified</>
                      ) : (
                        <><AlertCircle className="w-3 h-3 mr-1" />Unverified</>
                      )}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>

            <Separator />

          </CardContent>
        </Card>

        {/* Account Balances */}
        {profile.balances && Object.keys(profile.balances).length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center space-x-2">
                <Wallet className="w-5 h-5" />
                <CardTitle>Account Balances</CardTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/payment-sources')}
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Funding Sources
              </Button>
            </CardHeader>
            <CardContent>
              {showMeMoneyBetaBalancesNotice ? (
                <div className="mb-4 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-50">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                  <p>{i18n.__('vaiu.profile_page.balances.memoney_beta_notice')}</p>
                </div>
              ) : null}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(profile.balances).map(([currency, balance]) => (
                  <div key={currency} className="p-4 border rounded-lg">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <Label className="text-sm font-medium text-muted-foreground">{currency}</Label>
                        <p className="text-2xl font-bold">
                          <CurrencyDisplay 
                            amount={balance as number} 
                            currency={currency}
                            currencyService={currencyService}
                            size="lg"
                          />
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/topup?currency=${encodeURIComponent(currency)}`)}
                          aria-label={`Top up ${currency}`}
                          title={`Top up ${currency}`}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/transfer-out/${encodeURIComponent(currency)}`)}
                          aria-label={`Transfer out ${currency}`}
                          title={`Transfer out ${currency}`}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Landing Page Preferences */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <Home className="w-5 h-5" />
              <CardTitle>Landing Page Preferences</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <LandingPreferenceSelector embedded={true} />
          </CardContent>
        </Card>

        {/* Security Settings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5" />
              <CardTitle>{i18n.__('vaiu.profile_page.security') || 'Security Settings'}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            
            {/* PIN Management */}
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-base font-medium">PIN Protection</Label>
                <p className="text-sm text-muted-foreground">
                  {profile.hasPin ? 'PIN is configured' : 'No PIN set'}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                {profile.hasPin && (
                  <Switch
                    checked={profile.pinEnabled}
                    onCheckedChange={togglePinEnabled}
                  />
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPinDialog(true)}
                >
                  {profile.hasPin ? 'Change PIN' : 'Set PIN'}
                </Button>
              </div>
            </div>

            {/* User Roles */}
            {profile.roles.length > 0 && (
              <div className="p-3 border rounded-lg">
                <Label className="text-base font-medium">Account Roles</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {profile.roles.map((role, index) => (
                    <Badge key={index} variant="outline">
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Privacy & Cookie Settings */}
            <div className="p-3 border rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <Label className="text-base font-medium">Privacy & Cookies</Label>
                  <p className="text-sm text-muted-foreground">
                    Manage your privacy preferences and cookie settings
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/privacy-policy')}
                    className="whitespace-nowrap"
                  >
                    Privacy Policy
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/cookie-settings')}
                    className="whitespace-nowrap"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Cookie Settings
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Developer Settings */}
            <div className="p-3 border rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Developer Access</Label>
                  <p className="text-sm text-muted-foreground">
                    I want to develop an app with Vaiu token services
                  </p>
                </div>
                {profile.roles.includes('DEVELOPER') ? (
                  <Button
                    onClick={() => navigate('/developer')}
                    className="flex items-center space-x-2"
                  >
                    <Code className="w-4 h-4" />
                    <span>Go to Developer Console</span>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setPendingDeveloperToggle(true);
                      setShowDeveloperTermsSection(!showDeveloperTermsSection);
                    }}
                    className="flex items-center space-x-2"
                  >
                    <FileText className="w-4 h-4" />
                    <span>{showDeveloperTermsSection ? 'Hide Terms' : 'See Terms of Access'}</span>
                  </Button>
                )}
              </div>
              
              {/* Developer Terms - Inline Display */}
              {showDeveloperTermsSection && !profile.roles.includes('DEVELOPER') && (
                <div className="mt-4 space-y-4 border-t pt-4">
                  <div className="border-2 border-blue-200 bg-blue-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-blue-600" />
                      <h4 className="font-semibold text-blue-900">Important Notice</h4>
                    </div>
                    <p className="text-blue-800 text-sm">
                      By accepting these terms, you will gain access to the Vaiu Developer Console, which includes 
                      advanced debugging tools, API testing capabilities, and sensitive system information.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold">Developer Console Access Includes:</h4>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start space-x-2">
                        <Code className="w-4 h-4 mt-0.5 text-blue-600" />
                        <span>API testing and debugging tools</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Settings className="w-4 h-4 mt-0.5 text-green-600" />
                        <span>Application configuration inspection</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <User className="w-4 h-4 mt-0.5 text-purple-600" />
                        <span>User data and environment information</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <Shield className="w-4 h-4 mt-0.5 text-orange-600" />
                        <span>System monitoring and diagnostics</span>
                      </li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4 bg-orange-50 p-3 rounded-r-lg">
                    <h4 className="font-semibold text-orange-900 mb-2">Responsibilities</h4>
                    <ul className="space-y-1 text-sm text-orange-800">
                      <li>• Use developer tools responsibly and ethically</li>
                      <li>• Do not access or modify data without proper authorization</li>
                      <li>• Report any security vulnerabilities discovered</li>
                      <li>• Comply with all applicable laws and regulations</li>
                      <li>• Respect user privacy and data protection requirements</li>
                    </ul>
                  </div>

                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                    <p>
                      <strong>Note:</strong> Developer access can be revoked at any time by disabling this toggle. 
                      Your usage of developer tools may be monitored and logged for security purposes.
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <Button variant="outline" onClick={cancelDeveloperTerms}>
                      Cancel
                    </Button>
                    <Button onClick={acceptDeveloperTerms} className="bg-blue-600 hover:bg-blue-700">
                      <Code className="w-4 h-4 mr-2" />
                      Accept Terms & Go to Developer Console
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
          </CardContent>
        </Card>

        {/* Phone Number Management */}
        <PhoneNumberManager 
          initialPhoneNumber={profile?.telephone?.number || null}
          initialVerified={profile?.telephone?.verified || false}
        />

        {/* Push Notification Settings */}
        <NotificationSettings />
        
        {/* Notification Event Preferences */}
        <NotificationEventPreferences />

        {/* Communication Preferences */}
        <CommunicationPreferences />

        {/* Biometric Authentication Settings */}
        <BiometricSettings />

        {/* Language Settings */}
        <LanguageSelector />

        {/* Addresses */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5" />
              <CardTitle>Addresses</CardTitle>

            </div>
            <Button 
              onClick={() => {
                setShowAddressForm(!showAddressForm);
                setEditingAddressId(null);
                // Set default coordinates to Los Angeles when opening form
                setAddressForm({ 
                  lat: 34.052235, 
                  lng: -118.243683, 
                  address: '', 
                  detail: '', 
                  isPrimary: false 
                });
              }}
              size="sm"
              variant={showAddressForm ? "outline" : "default"}
            >
              {showAddressForm ? (
                <><X className="w-4 h-4 mr-2" />Cancel</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" />Add Address</>
              )}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            

            
            {/* Inline Address Form */}
            {showAddressForm && (
              <div className="p-4 border-2 border-dashed border-primary/20 rounded-lg bg-muted/20 space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <MapPin className="w-4 h-4 text-primary" />
                  <h4 className="font-medium text-primary">
                    {editingAddressId ? 'Edit Address' : 'Add New Address'}
                  </h4>
                </div>
                
                <div>
                  <Label htmlFor="address-manual-input">Address</Label>
                  <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                    <Input
                      id="address-manual-input"
                      value={addressForm.address}
                      onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                      placeholder="Enter your street address"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => geocodeAddressText(addressForm.address)}
                    >
                      Find on map
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Type your address and click Find on map, or click or drag the pin on the map below
                  </p>
                  
                  {/* Manual coordinate entry fallback */}
                  {process.env.NODE_ENV === 'development' && (
                    <details className="mt-2">
                      <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                        🔧 Manual Coordinate Entry (Debug)
                      </summary>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Latitude</Label>
                          <Input
                            type="number"
                            step="any"
                            value={addressForm.lat}
                            onChange={(e) => setAddressForm({ ...addressForm, lat: parseFloat(e.target.value) || 0 })}
                            className="text-xs h-8"
                            placeholder="34.052235"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Longitude</Label>
                          <Input
                            type="number"
                            step="any"
                            value={addressForm.lng}
                            onChange={(e) => setAddressForm({ ...addressForm, lng: parseFloat(e.target.value) || 0 })}
                            className="text-xs h-8"
                            placeholder="-118.243683"
                          />
                        </div>
                      </div>
                    </details>
                  )}
                </div>
                
                <div 
                  id="map" 
                  className="w-full h-64 border rounded-lg bg-slate-100"
                  style={{ 
                    minHeight: '256px', 
                    width: '100%', 
                    position: 'relative',
                    backgroundColor: '#f1f5f9' 
                  }}
                  ref={(el) => {
                    if (el && showAddressForm && !mapInstanceRef.current) {
                      if (window.google && window.google.maps) {
                        initializeMap(el);
                      } else {
                        console.warn('⚠️ Google Maps API not yet available');
                        el.innerHTML = `
                          <div style="
                            display: flex; 
                            align-items: center; 
                            justify-content: center; 
                            height: 256px; 
                            background: #f1f5f9;
                            border: 2px dashed #cbd5e1;
                            border-radius: 8px;
                            flex-direction: column;
                            color: #64748b;
                            font-size: 14px;
                            text-align: center;
                            padding: 20px;
                          ">
                            <div style="margin-bottom: 8px;">🗺️</div>
                            <div style="font-weight: 500;">Loading Google Maps...</div>
                            <div style="font-size: 12px; margin-top: 4px;">If this persists, check your internet connection</div>
                          </div>
                        `;
                        setTimeout(() => {
                          if (window.google && window.google.maps && !mapInstanceRef.current) {
                            initializeMap(el);
                          }
                        }, 2000);
                      }
                    }
                  }}
                ></div>
                
                <div>
                  <Label htmlFor="detail">Additional Details</Label>
                  <Textarea
                    id="detail"
                    value={addressForm.detail}
                    onChange={(e) => setAddressForm({ ...addressForm, detail: e.target.value })}
                    placeholder="Apartment, suite, floor, etc."
                    className="mt-1"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isPrimary"
                    checked={addressForm.isPrimary}
                    onCheckedChange={(checked) => 
                      setAddressForm({ ...addressForm, isPrimary: checked })
                    }
                  />
                  <Label htmlFor="isPrimary">Set as primary address</Label>
                </div>
                
                {/* Debug: Always show coordinates in development */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="text-xs text-blue-600 border border-blue-200 p-2 rounded space-y-1">
                    <div><strong>🗺️ DEBUG INFO:</strong></div>
                    <div>📍 Coordinates = {addressForm.lat.toFixed(6)}, {addressForm.lng.toFixed(6)}</div>
                    <div>🏠 Address = "{addressForm.address}"</div>
                    <div>🚫 Button disabled = {(!addressForm.address || (addressForm.lat === 0 && addressForm.lng === 0)).toString()}</div>
                    <div>❓ Has address: {!!addressForm.address ? '✅' : '❌'}</div>
                    <div>❓ Has coordinates: {(addressForm.lat !== 0 || addressForm.lng !== 0) ? '✅' : '❌'}</div>
                    <div className="text-green-600 font-medium">💡 Try: Click on the map OR search for an address</div>
                  </div>
                )}
                
                {addressForm.lat !== 0 && addressForm.lng !== 0 && (
                  <div className="text-xs text-muted-foreground">
                    Coordinates: {addressForm.lat.toFixed(6)}, {addressForm.lng.toFixed(6)}
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddressForm(false)}
                    size="sm"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={saveAddress}
                    disabled={!addressForm.address || (addressForm.lat === 0 && addressForm.lng === 0)}
                    size="sm"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingAddressId ? 'Update' : 'Add'} Address
                  </Button>
                </div>
              </div>
            )}

            {/* Address List */}
            {profile.addresses?.length === 0 && !showAddressForm ? (
              <p className="text-center text-muted-foreground py-8">No addresses added yet</p>
            ) : (
              (profile.addresses ?? []).map((address) => (
                <div key={address.id} className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="font-medium">{address.address}</p>
                        {address.isPrimary && (
                          <Badge variant="default" className="text-xs">
                            <Star className="w-3 h-3 mr-1" />
                            Primary
                          </Badge>
                        )}
                      </div>
                      {address.detail && (
                        <p className="text-sm text-muted-foreground mt-1">{address.detail}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {address.lat.toFixed(6)}, {address.lng.toFixed(6)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {!address.isPrimary && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPrimaryAddress(address.id)}
                        >
                          Set Primary
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingAddressId(address.id);
                          setAddressForm({
                            lat: address.lat,
                            lng: address.lng,
                            address: address.address,
                            detail: address.detail,
                            isPrimary: address.isPrimary || false
                          });
                          setShowAddressForm(true);
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteAddress(address.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <ProfileTransferOutPayoutsCard
          profile={profile}
          editing={editingSection === 'transfer-out'}
          onEditingChange={(open) => setEditingSection(open ? 'transfer-out' : null)}
          onRefresh={loadProfile}
          onAfterPaypalVerify={
            refreshAuthProfile && token
              ? async () => {
                  await refreshAuthProfile(token);
                }
              : undefined
          }
          cardId="transfer-out"
          className="scroll-mt-24"
        />

        {/* Auto-Deposit Preferences */}
        <Card id="auto-deposit">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <PiggyBank className="w-5 h-5" />
              <CardTitle>Auto-Deposit Preferences</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingSection(editingSection === 'auto-deposit' ? null : 'auto-deposit');
                setTempData({
                  autoDepositEnabled: Boolean(autoDepositPreferences.autoDepositEnabled),
                  autoDepositThreshold: autoDepositPreferences.autoDepositThreshold || 0,
                });
              }}
            >
              {editingSection === 'auto-deposit' ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <CardDescription>
              Automatically convert received cash tokens to your account balance when enabled.
            </CardDescription>
            
            {editingSection === 'auto-deposit' ? (
              <div className="space-y-6">
                <div className="space-y-3 rounded-lg border p-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={Boolean(tempData.autoDepositEnabled)}
                      onCheckedChange={(checked) =>
                        setTempData((prev) => ({ ...prev, autoDepositEnabled: Boolean(checked) }))
                      }
                    />
                    <div>
                      <Label className="text-base font-medium">Enable Auto-Deposit</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically deposit cash tokens to your balance
                      </p>
                    </div>
                  </div>
                  {!tempData.autoDepositEnabled && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
                        <div className="text-sm">
                          <p className="font-medium text-amber-900 dark:text-amber-100">
                            When auto-deposit is off:
                          </p>
                          <p className="text-amber-800 dark:text-amber-200 mt-1">
                            Incoming cash tokens will be held in your Action Inbox for manual approval. 
                            You'll need to review and accept each payment before it's deposited to your balance.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {tempData.autoDepositEnabled && (
                  <div className="space-y-3">
                    <Label htmlFor="autoDepositThreshold" className="text-base font-medium">
                      Auto-Deposit Threshold ({profile?.currency || 'USD'})
                    </Label>
                    <Input
                      id="autoDepositThreshold"
                      type="number"
                      step="0.01"
                      min="0"
                      value={tempData.autoDepositThreshold !== undefined ? tempData.autoDepositThreshold : ''}
                      onChange={(e) => setTempData({ ...tempData, autoDepositThreshold: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                      placeholder="0.00"
                      className="max-w-md"
                    />
                    <p className="text-sm text-muted-foreground">
                      Auto-deposit tokens up to this amount. Tokens above this threshold will require manual review.
                    </p>
                  </div>
                )}

                <div className="flex space-x-2">
                  <Button onClick={updateAutoDepositPreferences} size="sm">
                    <Save className="w-4 h-4 mr-2" />
                    {i18n.__('common.save') || 'Save Preferences'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setEditingSection(null)}
                  >
                    {i18n.__('common.cancel') || 'Cancel'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <PiggyBank className="w-5 h-5 text-green-600" />
                    <div>
                      <Label className="text-base font-medium">Auto-Deposit Status</Label>
                      <p className="text-sm text-muted-foreground">
                        {autoDepositPreferences.autoDepositEnabled ? 'Enabled' : 'Disabled'}
                      </p>
                    </div>
                  </div>
                  <Badge variant={autoDepositPreferences.autoDepositEnabled ? "default" : "secondary"}>
                    {autoDepositPreferences.autoDepositEnabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>

                {!autoDepositPreferences.autoDepositEnabled && (
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-900 dark:text-amber-100">
                          Manual Review Mode Active
                        </p>
                        <p className="text-amber-800 dark:text-amber-200 mt-1">
                          All incoming cash tokens are being held in your Action Inbox for approval. 
                          You'll need to manually review and accept each payment before it's deposited to your balance.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {autoDepositPreferences.autoDepositEnabled && (
                  <div className="p-3 border rounded-lg">
                    <Label className="text-base font-medium">Auto-Deposit Threshold</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      <CurrencyDisplay amount={autoDepositPreferences.autoDepositThreshold || 0} currency={profile?.currency || 'USD'} currencyService={currencyService} />
                      {(autoDepositPreferences.autoDepositThreshold || 0) === 0 && ' (All tokens)'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auto-Accept Preferences */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{i18n.__('vaiu.profile.autoAccept') || 'Token Auto-Accept'}</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingSection(editingSection === 'auto-accept' ? null : 'auto-accept');
                setTempData({
                  autoAcceptTokens: autoAcceptPreferences.autoAcceptTokens,
                });
              }}
            >
              {editingSection === 'auto-accept' ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <CardDescription>
              Control how tokens are redeemed when you click on token links or scan QR codes.
            </CardDescription>
            
            {editingSection === 'auto-accept' ? (
              <div className="space-y-6">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Switch
                      checked={tempData.autoAcceptTokens}
                      onCheckedChange={(checked) => setTempData({ ...tempData, autoAcceptTokens: checked })}
                    />
                    <div>
                      <Label className="text-base font-medium">Enable Auto-Accept</Label>
                      <p className="text-sm text-muted-foreground">
                        Automatically redeem tokens without showing the reveal screen
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div className="text-sm text-muted-foreground">
                      <p>When enabled:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Tokens will be instantly redeemed to your balance</li>
                        <li>You won't see token details before redemption</li>
                        <li>Partial redemption won't be available</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button onClick={updateAutoAcceptPreferences} size="sm">
                    <Save className="w-4 h-4 mr-2" />
                    {i18n.__('common.save') || 'Save Preferences'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setEditingSection(null)}
                  >
                    {i18n.__('common.cancel') || 'Cancel'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <Label className="text-base font-medium">Auto-Accept Status</Label>
                      <p className="text-sm text-muted-foreground">
                        {autoAcceptPreferences.autoAcceptTokens ? 'Tokens are automatically redeemed' : 'Manual review before redemption'}
                      </p>
                    </div>
                  </div>
                  <Badge variant={autoAcceptPreferences.autoAcceptTokens ? "default" : "secondary"}>
                    {autoAcceptPreferences.autoAcceptTokens ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoice Payment Preferences */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>{i18n.__('vaiu.profile.invoicePayment') || 'Invoice Payment Preferences'}</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEditingSection(editingSection === 'invoice-payment' ? null : 'invoice-payment');
                setTempData({
                  mode: invoicePaymentPreferences.mode,
                  threshold: invoicePaymentPreferences.threshold || 0,
                });
              }}
            >
              {editingSection === 'invoice-payment' ? <X className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            <CardDescription>
              Control how invoice tokens are handled when you receive them.
            </CardDescription>
            
            {editingSection === 'invoice-payment' ? (
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label className="text-base font-medium">Payment Mode</Label>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id="auto-pay"
                        name="invoiceMode"
                        value="auto-pay"
                        checked={tempData.mode === 'auto-pay'}
                        onChange={(e) => setTempData({ ...tempData, mode: e.target.value })}
                        className="h-4 w-4"
                      />
                      <label htmlFor="auto-pay" className="flex-1 cursor-pointer">
                        <div>
                          <p className="font-medium">Auto-pay all invoices</p>
                          <p className="text-sm text-muted-foreground">Automatically pay invoices without asking</p>
                        </div>
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id="always-ask"
                        name="invoiceMode"
                        value="always-ask"
                        checked={tempData.mode === 'always-ask'}
                        onChange={(e) => setTempData({ ...tempData, mode: e.target.value })}
                        className="h-4 w-4"
                      />
                      <label htmlFor="always-ask" className="flex-1 cursor-pointer">
                        <div>
                          <p className="font-medium">Always ask</p>
                          <p className="text-sm text-muted-foreground">Review and approve each invoice payment</p>
                        </div>
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <input
                        type="radio"
                        id="ask-if-above"
                        name="invoiceMode"
                        value="ask-if-above"
                        checked={tempData.mode === 'ask-if-above'}
                        onChange={(e) => setTempData({ ...tempData, mode: e.target.value })}
                        className="h-4 w-4"
                      />
                      <label htmlFor="ask-if-above" className="flex-1 cursor-pointer">
                        <div>
                          <p className="font-medium">Ask if amount is above threshold</p>
                          <p className="text-sm text-muted-foreground">Auto-pay small invoices, ask for larger ones</p>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {tempData.mode === 'ask-if-above' && (
                  <div className="space-y-3">
                    <Label htmlFor="invoiceThreshold" className="text-base font-medium">
                      Approval Threshold ({profile?.currency || 'USD'})
                    </Label>
                    <Input
                      id="invoiceThreshold"
                      type="number"
                      step="0.01"
                      min="0"
                      value={tempData.threshold !== undefined ? tempData.threshold : ''}
                      onChange={(e) => setTempData({ ...tempData, threshold: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                      placeholder="0.00"
                      className="max-w-md"
                    />
                    <p className="text-sm text-muted-foreground">
                      Invoices above this amount will require your approval before payment.
                    </p>
                  </div>
                )}

                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div className="text-sm text-muted-foreground">
                      <p>Invoice payment settings apply when:</p>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>You receive an invoice token</li>
                        <li>Someone sends you a payment request</li>
                        <li>You scan an invoice QR code</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button onClick={updateInvoicePaymentPreferences} size="sm">
                    <Save className="w-4 h-4 mr-2" />
                    {i18n.__('common.save') || 'Save Preferences'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setEditingSection(null)}
                  >
                    {i18n.__('common.cancel') || 'Cancel'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <Label className="text-base font-medium">Invoice Payment Mode</Label>
                      <p className="text-sm text-muted-foreground">
                        {invoicePaymentPreferences.mode === 'auto-pay' && 'Auto-pay all invoices'}
                        {invoicePaymentPreferences.mode === 'always-ask' && 'Always ask before paying'}
                        {invoicePaymentPreferences.mode === 'ask-if-above' && 
                          `Ask if above ${invoicePaymentPreferences.threshold || 0} ${profile?.currency || 'USD'}`}
                      </p>
                    </div>
                  </div>
                  <Badge variant={invoicePaymentPreferences.mode === 'auto-pay' ? "default" : "secondary"}>
                    {invoicePaymentPreferences.mode === 'auto-pay' ? "Auto-pay" : 
                     invoicePaymentPreferences.mode === 'always-ask' ? "Manual" : "Threshold"}
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Merchant Settings */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <Building className="w-5 h-5" />
              <CardTitle>Business Settings</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <Label className="text-base font-medium">Merchant Account</Label>
                <p className="text-sm text-muted-foreground">
                  {hasMerchantRole
                    ? 'Merchant features are on for this account. You can turn them off or use the tools below.'
                    : 'Enable merchant features for your account'}
                </p>
              </div>
              <Switch
                checked={hasMerchantRole}
                onCheckedChange={toggleMerchantStatus}
                aria-label="Merchant account"
              />
            </div>

            {hasMerchantRole && (
              <div className="p-3 border rounded-lg">
                <div className="space-y-3">
                  <div>
                    <Label className="text-base font-medium">Merchant Profile</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Merchant features are enabled. You can accept payments and manage your business profile.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/profile/merchant')}
                      className="flex items-center space-x-2"
                    >
                      <Building className="w-4 h-4" />
                      <span>Profile Settings</span>
                    </Button>
                    <Button
                      onClick={() => navigate('/merchant-tools')}
                      className="flex items-center space-x-2"
                    >
                      <ShoppingBag className="w-4 h-4" />
                      <span>Business Tools</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Account Information */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5" />
              <CardTitle>Account Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Member Since</Label>
              <p className="text-base">{formatDate(profile.createdAt)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Last Login</Label>
              <p className="text-base">{formatDate(profile.lastLoginAt)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Currency</Label>
              <p className="text-base">{profile.currency}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Verification Status</Label>
              <Badge variant={profile.isVerified ? "default" : "secondary"}>
                {profile.isVerified ? "Fully Verified" : "Pending Verification"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Debug Information (development only) */}
        {process.env.NODE_ENV === 'development' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center space-x-2">
                <Globe className="w-5 h-5" />
                <CardTitle>Debug Information</CardTitle>
              </div>
              <CardDescription>Raw profile data (development only)</CardDescription>
            </CardHeader>
            <CardContent>
              <pre className="text-xs bg-slate-100 p-3 rounded-md overflow-auto max-h-60">
                {JSON.stringify(profile, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}

        {/* MeMoney (MM$) issuer artwork — user profile (see MM_MONEY_CONTRACT.md) */}
        <section id="memoney" className="space-y-2 scroll-mt-4" aria-label="MeMoney MM$ appearance">
          <MmMoneyAppearanceCard
            toast={meMoneyToast}
            locale={i18n.getLanguage() as MeMoneyLocale}
            mmMoneyBranding={mmMoneyBrandingHandlers}
            uploadImage={mmMoneyUploadImage}
            previewIssuerUserId={resolveAuthUserId(profile) ?? undefined}
          />
        </section>

        {/* Brand Theme Settings */}
        <BrandThemeSelector />



      </div>

      {/* PIN Dialog */}
      <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{profile.hasPin ? 'Change PIN' : 'Set PIN'}</DialogTitle>
            <DialogDescription>
              {profile.hasPin 
                ? 'Enter your current PIN and choose a new one'
                : 'Choose a 4-6 digit PIN to secure your account'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {profile.hasPin && (
              <div>
                <Label htmlFor="oldPin">Current PIN</Label>
                <Input
                  id="oldPin"
                  type="password"
                  value={pinData.oldPin}
                  onChange={(e) => setPinData({ ...pinData, oldPin: e.target.value })}
                  placeholder="Enter current PIN"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="newPin">New PIN</Label>
              <Input
                id="newPin"
                type="password"
                value={pinData.newPin}
                onChange={(e) => setPinData({ ...pinData, newPin: e.target.value })}
                placeholder="Enter new PIN (4-6 digits)"
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPin">Confirm New PIN</Label>
              <Input
                id="confirmPin"
                type="password"
                value={pinData.confirmPin}
                onChange={(e) => setPinData({ ...pinData, confirmPin: e.target.value })}
                placeholder="Confirm new PIN"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPinDialog(false)}>
              Cancel
            </Button>
            <Button onClick={updatePin}>
              {profile.hasPin ? 'Update PIN' : 'Set PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ProfilePage; 
